import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { hexToRGB, toRenderableHtml } from 'utils';
import styled from 'styled-components';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { Button, Icon, SubTitle, Loading } from 'components';
import { ColDiv, Container, SubWrap, RowDiv, Text, Pre, ContentBody } from 'styles';
import useDownload from 'hooks/useDownload';
import QnaPasswordInput from 'components/pages/community/qna/QnaPasswordInput';

import { BOARD_QNA_MODULE_NO } from 'data/constants';

import boardService from 'services/board/board-service';
import userAuthenticationStore from 'stores/authentication-store';

import { Alert } from 'components/modal/Alert';
import { Confirm } from 'components/modal/Confirm';

const PasswordFromEnum = {
  INIT: 'INIT',
  MODIFY: 'MODIFY',
  DELETE: 'DELETE'
};

const htmlToText = (html) =>
  (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '');

export function QnaDetail(props) {
  const location = useLocation();
  const { contentNo } = location.state || {};

  const { langList } = useOutletContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { downloadBySiteFileId } = useDownload();

  const [qna, setQna] = useState({});
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [password, setPassword] = useState('');
  const [alert, setAlert] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [passwordFrom, setPasswordFrom] = useState('');

  const userInfo = userAuthenticationStore((s) => s.userInfo);
  const isAdmin = !!(userInfo && (userInfo.adminYn === true || userInfo.id === 'farmin0130'));
  // 답변 수정 { commentNo, content }
  const [editState, setEditState] = useState(null);
  const [reply, setReply] = useState('');

  const alertMsg = (content) => setAlert({ content, confirm: { onClick: () => setAlert(null) } });

  const toggleShowPasswordInput = (show, form) => {
    if (show) {
      setShowPasswordInput(true);
      setShowContent(false);
      setPasswordFrom(form);
    } else {
      setShowPasswordInput(false);
      setShowContent(true);
      setPasswordFrom('');
    }
  };

  const loadComments = async () => {
    try {
      const commentList = await boardService.listComments(BOARD_QNA_MODULE_NO, contentNo);
      setComments(Array.isArray(commentList) ? commentList : []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchQna = async (pw) => {
    let data = {};

    try {
      const response = await boardService.getContent(BOARD_QNA_MODULE_NO, contentNo, pw);

      if (response) {
        data = response;
      }
    } catch (e) {
      console.error(e);
    }

    if (data.canAccess) {
      setQna(data.content || {});
      toggleShowPasswordInput(false);
      await loadComments();
      setLoading(false);
    } else {
      setLoading(false);
      toggleShowPasswordInput(true, PasswordFromEnum.INIT);
      setPassword('');
      alertMsg('비밀번호가 일치하지 않습니다.');
    }
  };

  const onSubmitPassword = () => {
    switch (passwordFrom) {
      case PasswordFromEnum.INIT: {
        if (password && password.length > 0) {
          setLoading(true);
          fetchQna(password);
        }
        break;
      }
      case PasswordFromEnum.MODIFY: {
        navigate('/community/qna/modify', { state: { contentNo: qna.contentNo, password } });
        break;
      }
      case PasswordFromEnum.DELETE: {
        setConfirm({
          content: '삭제하시겠습니까?',
          cancel: {
            onClick: () => {
              setConfirm(null);
              toggleShowPasswordInput(false);
            }
          },
          confirm: {
            onClick: async () => {
              await boardService.deleteContent(BOARD_QNA_MODULE_NO, contentNo);

              navigate('/community/qna');
            }
          }
        });
        break;
      }
      default: {
        break;
      }
    }
  };

  // ===== 관리자 답변 관리 (로그인 세션 기반) =====
  const submitReply = async () => {
    if (!reply.trim()) {
      alertMsg('답변 내용을 입력해주세요.');
      return;
    }
    const res = await boardService.adminAddComment(contentNo, reply);
    if (res && res.success) {
      setReply('');
      await loadComments();
    } else {
      alertMsg('답변 등록에 실패했습니다. 다시 로그인해 주세요.');
    }
  };

  const submitEdit = async () => {
    if (!editState || !editState.content.trim()) {
      alertMsg('답변 내용을 입력해주세요.');
      return;
    }
    const res = await boardService.adminUpdateComment(editState.commentNo, editState.content);
    if (res && res.success) {
      setEditState(null);
      await loadComments();
    } else {
      alertMsg('답변 수정에 실패했습니다. 다시 로그인해 주세요.');
    }
  };

  const onDelete = (com) => {
    setConfirm({
      content: '이 답변을 삭제하시겠습니까?',
      cancel: { onClick: () => setConfirm(null) },
      confirm: {
        onClick: async () => {
          setConfirm(null);
          const res = await boardService.adminDeleteComment(com.commentNo);
          if (res && res.success) {
            await loadComments();
          } else {
            alertMsg('답변 삭제에 실패했습니다. 다시 로그인해 주세요.');
          }
        }
      }
    });
  };

  React.useEffect(() => {
    async function init() {
      if (!contentNo) {
        navigate('/community/qna');
        return;
      }

      // 관리자는 비밀글도 비밀번호 없이 바로 열람
      if (isAdmin) {
        let data = {};
        try {
          const res = await boardService.adminGetContent(BOARD_QNA_MODULE_NO, contentNo);
          if (res) data = res;
        } catch (e) {
          console.error(e);
        }
        if (data.content) {
          setQna(data.content);
          toggleShowPasswordInput(false);
          await loadComments();
        }
        setLoading(false);
        return;
      }

      let isPrivateContent = false;

      try {
        const publicityResponse = await boardService.getContentPublicity(BOARD_QNA_MODULE_NO, contentNo);

        if (publicityResponse) {
          isPrivateContent = publicityResponse.isPrivateContent;
        }
      } catch (e) {
        console.error(e);
      }

      if (isPrivateContent) {
        setLoading(false);
        toggleShowPasswordInput(true, PasswordFromEnum.INIT);
      } else {
        fetchQna('');
      }
    }

    init();

    return () => {
      setLoading(true);
      setShowContent(false);
      setShowPasswordInput(false);
      setPasswordFrom('');
      setPassword('');
      setEditState(null);
      setReply('');
    };
  }, [contentNo, isAdmin]);

  return (
    <>
      {loading && <Loading />}
      {alert && <Alert {...alert} />}
      {confirm && <Confirm {...confirm} />}
      {showPasswordInput && <QnaPasswordInput password={password} setPassword={setPassword} obSubmit={onSubmitPassword} />}
      {showContent && (
        <Container $gap={48} $width="100%">
          <SubWrap $type="first">
            <SubTitle title={langList.qna.subject} desc={langList.qna.subjectDesc} />
            <ContentBox>
              <TitleBox $padding="48px" $gap={16} $mPadding="24px" $mGap={8}>
                <Text $type="h4" $mType="h5">
                  {qna.title}
                </Text>
                <RowDiv $alignItems="center" $gap={16}>
                  <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.4)} $fontWeight={500}>
                    {qna.regUserName}
                  </Text>
                  <Line />
                  <RowDiv $gap={8} $alignItems="center">
                    <Icon name="clockPrimary" $width={14} $height={14} />
                    <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.4)} $fontWeight={500}>
                      {qna.regDate ? dayjs(qna.regDate).format(t('date_format')) : ''}
                    </Text>
                  </RowDiv>
                </RowDiv>
              </TitleBox>
              <ColDiv $padding="48px" $mPadding="24px" $gap={48} $mGap={24}>
                <Pre $type="b6" $mType="b7">
                  {qna.content}
                </Pre>
                {qna.attachFiles && qna.attachFiles.length > 0 && (
                  <FileBox $gap={8}>
                    {qna.attachFiles.map((file, index) => (
                      <RowDiv
                        key={`file_${index}`}
                        onClick={() => {
                          downloadBySiteFileId(file.siteFileId, file.originalFileName);
                        }}
                      >
                        <RowDiv $alignItems="center">
                          <Icon name="file" $width={15} $height={16} $padding="4px 4px 4px 5px" />
                          <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.4)} $fontWeight={500}>
                            {langList.notice.attachedFile}
                          </Text>
                        </RowDiv>
                        <RowDiv $alignItems="center">
                          <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.87)} $fontWeight={500}>
                            {file.originalFileName}
                          </Text>
                        </RowDiv>
                      </RowDiv>
                    ))}
                  </FileBox>
                )}
              </ColDiv>

              {(comments.length > 0 || isAdmin) && (
                <CommentWrap>
                  {comments.map((com) => (
                    <CommentBox key={`comment_${com.commentNo}`}>
                      <RowDiv $justifyContent="space-between" $alignItems="center">
                        <RowDiv $gap={8} $alignItems="center">
                          <Icon name="comment" $width={18} $height={18} $padding="0" />
                          <Text $fontWeight={700} $type="b7" $mType="b8" $color="primary">
                            {com.regUserName || '팜인 관리자'}
                          </Text>
                          <ReplyBadge>답변</ReplyBadge>
                        </RowDiv>
                        <Text $fontWeight={500} $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.4)}>
                          {com.regDate ? dayjs(com.regDate).format(t('date_format')) : ''}
                        </Text>
                      </RowDiv>

                      {editState && editState.commentNo === com.commentNo ? (
                        <AdminPanel>
                          <AdminTextarea
                            value={editState.content}
                            onChange={(e) => setEditState({ ...editState, content: e.target.value })}
                            placeholder="답변 내용을 입력하세요."
                          />
                          <RowDiv $gap={8} $justifyContent="flex-end">
                            <Button label="취소" btnStyle="cancel" tBtnType="medium" onClick={() => setEditState(null)} />
                            <Button label="저장" btnStyle="fill" tBtnType="medium" onClick={submitEdit} />
                          </RowDiv>
                        </AdminPanel>
                      ) : (
                        <>
                          <CommentText>
                            <ContentBody $small $color={hexToRGB('#171717', 0.87)} dangerouslySetInnerHTML={{ __html: toRenderableHtml(com.content) }} />
                          </CommentText>
                          {isAdmin && (
                            <RowDiv $gap={12} $justifyContent="flex-end">
                              <TextBtn type="button" onClick={() => setEditState({ commentNo: com.commentNo, content: htmlToText(com.content) })}>
                                수정
                              </TextBtn>
                              <TextBtn type="button" onClick={() => onDelete(com)}>
                                삭제
                              </TextBtn>
                            </RowDiv>
                          )}
                        </>
                      )}
                    </CommentBox>
                  ))}

                  {isAdmin && (
                    <CommentBox>
                      <RowDiv $gap={8} $alignItems="center">
                        <Icon name="comment" $width={18} $height={18} $padding="0" />
                        <Text $fontWeight={700} $type="b7" $mType="b8" $color="primary">
                          관리자 답변 작성
                        </Text>
                      </RowDiv>
                      <AdminPanel>
                        <AdminTextarea
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          placeholder="질문에 대한 답변을 입력하세요."
                        />
                        <RowDiv $gap={8} $justifyContent="flex-end">
                          <Button label="답변 등록" btnStyle="fill" tBtnType="medium" onClick={submitReply} />
                        </RowDiv>
                      </AdminPanel>
                    </CommentBox>
                  )}
                </CommentWrap>
              )}
            </ContentBox>
            <BtnBox $justifyContent="space-between">
              {qna.previousContent ? (
                <RowDiv
                  $hover
                  $alignItems="center"
                  $gap={16}
                  $mGap={4}
                  onClick={() => {
                    navigate('/community/qna/view', { state: { contentNo: qna.previousContent.contentNo } });
                  }}
                >
                  <Icon name="arrow" $transform="rotate(90deg)" />
                  <Text $type="b7" $color="darker" $maxLine={1}>
                    {langList.notice.prev}
                  </Text>
                </RowDiv>
              ) : (
                <RowDiv $hover $alignItems="center" $gap={16} $mGap={4}></RowDiv>
              )}
              <RowDiv $gap={16}>
                {qna.useSecret && (
                  <>
                    <Button label="삭제" btnStyle="cancel" tBtnType="medium" onClick={() => toggleShowPasswordInput(true, PasswordFromEnum.DELETE)} />
                    <Button label="수정" btnStyle="fill" tBtnType="medium" onClick={() => toggleShowPasswordInput(true, PasswordFromEnum.MODIFY)} />
                  </>
                )}
                <Button label="목록" btnStyle="darkLine" tBtnType="medium" onClick={() => navigate(-1)} />
              </RowDiv>
              {qna.nextContent ? (
                <RowDiv
                  $hover
                  $alignItems="center"
                  $gap={16}
                  $mGap={4}
                  onClick={() => {
                    navigate('/community/qna/view', { state: { contentNo: qna.nextContent.contentNo } });
                  }}
                >
                  <Text $type="b7" $color="darker" $maxLine={1}>
                    {langList.notice.next}
                  </Text>
                  <Icon name="arrow" $transform="rotate(-90deg)" />
                </RowDiv>
              ) : (
                <RowDiv $hover $alignItems="center" $gap={16} $mGap={4}></RowDiv>
              )}
            </BtnBox>
          </SubWrap>
        </Container>
      )}
    </>
  );
}
const TitleBox = styled(ColDiv)`
  border-bottom: 1px solid ${hexToRGB('#171717', 0.12)};
`;

const ContentBox = styled.div`
  border-top: 2px solid ${({ theme }) => theme.colors.primary900};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary900};
`;

const FileBox = styled(ColDiv)`
  & div {
    background-color: ${({ theme }) => theme.colors.lightest};
    cursor: pointer;
  }
  & div > div {
    padding: 16px 24px;
  }
  @media ${({ theme }) => theme.device.mobile} {
    & div > div {
      padding: 16px 4px;
    }
  }
`;

const Line = styled.span`
  height: 12px;
  width: 1px;
  background-color: ${({ theme }) => hexToRGB(theme.colors.strong, 0.4)};
`;

const CommentWrap = styled.div`
  border-top: 1px solid ${({ theme }) => hexToRGB(theme.colors.strong, 0.12)};
  background: ${({ theme }) => hexToRGB(theme.colors.primary900, 0.03)};
`;

const CommentBox = styled(ColDiv)`
  padding: 32px 48px;
  gap: 16px;

  & + & {
    border-top: 1px solid ${({ theme }) => hexToRGB(theme.colors.strong, 0.12)};
  }

  @media ${({ theme }) => theme.device.mobile} {
    padding: 16px 24px;
    gap: 8px;
  }
`;

const ReplyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: ${({ theme }) => theme.colors.primary900};
`;

const CommentText = styled.div`
  padding-left: 26px;

  @media ${({ theme }) => theme.device.mobile} {
    padding-left: 0;
  }
`;

const TextBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => hexToRGB(theme.colors.strong, 0.5)};

  &:hover {
    color: ${({ theme }) => theme.colors.primary900};
    text-decoration: underline;
  }
`;

const AdminPanel = styled(ColDiv)`
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ theme }) => hexToRGB(theme.colors.strong, 0.15)};
  border-radius: 8px;
  background: #fff;
`;

const AdminTextarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 12px;
  border: 1px solid ${({ theme }) => hexToRGB(theme.colors.strong, 0.2)};
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
`;

const BtnBox = styled(RowDiv)`
  @media ${({ theme }) => theme.device.mobile} {
    position: relative;

    & div {
      max-width: 82px;
    }
    & > button {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: -62px;
    }
  }
`;
